'use client'

import dynamic from 'next/dynamic'
import { useEffect, useRef } from 'react'

import usePx from '@src/quince-site-common/src/hooks/usePx'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import type { TelemetryEventNames } from '@src/telemetry/telemetry-events.types'
import { trackEvent } from '@src/telemetry/track'

interface QRCanvasBaseProps {
  url: string
  className?: string
  /** Quiet-zone size in modules (default 4). */
  margin?: number
}

/** Exactly one of `widthInPx` | `widthInRem` — the union requires (and allows only) one. */
type QRCanvasProps = QRCanvasBaseProps &
  (
    | {
        /** Fixed CSS px. Prefer on hybrid legacy pages (fluid rem root shrinks rem-based QRs). */
        widthInPx: number
        widthInRem?: never
      }
    | {
        /** Sized from the live root font-size. */
        widthInRem: number
        widthInPx?: never
      }
  )

function QRCanvasComponent({ url, widthInRem, widthInPx: widthInPxProp, className, margin = 4 }: QRCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Dedupe telemetry across rem→px settle / resize re-paints of the same url.
  const trackedOutcomesRef = useRef(new Set<string>())
  const getPxFromRem = usePx()
  const widthInPx = typeof widthInPxProp === 'number' ? widthInPxProp : getPxFromRem(widthInRem, false)

  useEffect(() => {
    const trackOnce = (event: TelemetryEventNames) => {
      const key = `${url}|${event}`
      if (trackedOutcomesRef.current.has(key)) return
      trackedOutcomesRef.current.add(key)
      trackEvent(event, {})
    }

    const generateQR = async () => {
      if (canvasRef.current) {
        const { toCanvas } = await import(/* webpackChunkName: "qrcode" */ 'qrcode')
        try {
          // Hi-dpi backing store; restore logical CSS size (qrcode sets both to the dpr width).
          const dpr = window.devicePixelRatio || 1
          await toCanvas(canvasRef.current, url, {
            width: widthInPx * dpr,
            margin,
          })
          canvasRef.current.style.width = `${widthInPx}px`
          canvasRef.current.style.height = `${widthInPx}px`
          trackOnce(TELEMETRY_EVENT_NAMES.QR_RENDERED_SUCCESS)
        } catch {
          trackOnce(TELEMETRY_EVENT_NAMES.QR_RENDERED_ERROR)
        }
      }
    }
    void generateQR()
  }, [url, widthInPx, margin])

  return <canvas ref={canvasRef} width={widthInPx} height={widthInPx} className={className} />
}

export default dynamic(() => Promise.resolve(QRCanvasComponent), {
  ssr: false,
})
